import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";

const PublicRequestViewScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const { t, isRTL, language } = useLanguage();
  const { requestId } = useParams<{ requestId: string }>();

  // Debug: Log the language and isRTL values
  console.log('PublicRequestView - Language:', language, 'isRTL:', isRTL);

  useEffect(() => {
    loadPublicRequestDetail();
  }, [requestId]);

  const loadPublicRequestDetail = async () => {
    if (!requestId) {
      setError(t("request_not_found"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${baseUrl}/clinic/registration/public/${requestId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError(t("request_not_found"));
        } else {
          setError(t("failed_to_load_request_details"));
        }
        setLoading(false);
        return;
      }

      const result = await response.json();
      setRequest(result.data || result);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed_to_load_request_details"));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (error || !request) {
    return (
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="⚠️"
        iconSize="w-24 h-24 text-6xl"
        title={t("error")}
        subtitle={error || t("request_not_found")}
        maxHeight="90vh"
      >
        <div className="text-center text-gray-600 mt-4">
          <p>{t("public_request_not_available")}</p>
        </div>
      </ResponsiveFormLayout>
    );
  }

  const InfoRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
    if (!value) return null;
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="flex justify-between items-start py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600 font-medium">{label}:</span>
        <span className="text-sm text-gray-900 font-semibold">{value}</span>
      </div>
    );
  };

  const getRequestTypeLabel = (type: string) => {
    if (type === 'registration') return t("clinic_registration");
    if (type === 'onboarding') return t("clinic_onboarding");
    return type;
  };

  const getStatusColor = (status: string) => {
    if (status === 'pending') return 'bg-yellow-50 border-yellow-200';
    if (status === 'draft') return 'bg-blue-50 border-blue-200';
    if (status === 'approved') return 'bg-green-50 border-green-200';
    if (status === 'rejected') return 'bg-red-50 border-red-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'pending') return '⏳';
    if (status === 'draft') return '📝';
    if (status === 'approved') return '✅';
    if (status === 'rejected') return '❌';
    return '📄';
  };

  // Helper function to translate data values
  const translateValue = (value: string | number | null | undefined): string => {
    if (!value) return '';

    const stringValue = String(value);

    // Try to translate common data values
    // Governorates - capitalize first letter for translation key
    const capitalizedValue = stringValue.charAt(0).toUpperCase() + stringValue.slice(1).toLowerCase();

    // Try capitalized version first (for governorates)
    if (t(capitalizedValue) !== capitalizedValue) {
      return t(capitalizedValue);
    }

    // Try lowercase version (for morning/evening)
    if (t(stringValue.toLowerCase()) !== stringValue.toLowerCase()) {
      return t(stringValue.toLowerCase());
    }

    // Return original value if no translation found
    return stringValue;
  };

  return (
    <>
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="📋"
        iconSize="w-24 h-24 text-6xl"
        title={t("request_details")}
        subtitle={t("public_view")}
        maxHeight="90vh"
      >
        <div className="w-full space-y-6">
          {/* Public Notice */}
          <div className="p-4 rounded-xl border border-blue-200 bg-blue-50">
            <div className="flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div className="flex-1">
                <p className="text-sm text-blue-900 font-medium">
                  {t("public_view_notice")}
                </p>
              </div>
            </div>
          </div>

          {/* Doctor Information Card */}
          {request.doctor && (
            <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-cyan-50 to-blue-50">
              <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
                👨‍⚕️ {t("doctor_information")}
              </h3>
              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-start gap-4">
                  {/* Doctor Photo */}
                  {request.doctor.profile_photo_url && (
                    <div className="flex-shrink-0">
                      <img
                        src={request.doctor.profile_photo_url}
                        alt={language === 'ar' ? request.doctor.full_name_ar : request.doctor.full_name}
                        className="w-24 h-24 rounded-full object-cover border-4 border-[#2E5D7D]"
                      />
                    </div>
                  )}

                  {/* Doctor Details */}
                  <div className="flex-1 space-y-1">
                    <InfoRow
                      label={t("doctor_name")}
                      value={language === 'ar' ? request.doctor.full_name_ar : request.doctor.full_name}
                    />
                    <InfoRow
                      label={t("medical_license_number")}
                      value={request.doctor.medical_license_number}
                    />
                    {request.doctor.practition && (
                      <InfoRow
                        label={t("practition")}
                        value={language === 'ar' ? request.doctor.practition.name_ar : request.doctor.practition.name}
                      />
                    )}
                    {request.doctor.specialization && (
                      <InfoRow
                        label={t("specialization")}
                        value={language === 'ar' ? request.doctor.specialization.name_ar : request.doctor.specialization.name}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Clinic Information */}
          {request.name && (
            <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
                🏥 {t("clinic_information")}
              </h3>
              <div className="space-y-1 bg-white p-4 rounded-lg">
                <InfoRow label={t("clinic_name")} value={request.name} />
                <InfoRow label={t("work_hours")} value={translateValue(request.clinic_work_hours)} />
              </div>
            </div>
          )}

          {/* Location Information */}
          {request.governorate && (
            <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
                📍 {t("location_information")}
              </h3>
              <div className="space-y-1 bg-white p-4 rounded-lg">
                <InfoRow label={t("governorate")} value={translateValue(request.governorate)} />
                <InfoRow label={t("city")} value={isRTL ? request.area?.name_ar : request.area?.name} />
                <InfoRow label={t("neighborhood")} value={request.neighborhood} />
                <InfoRow label={t("street")} value={request.street} />
                <InfoRow label={t("house_number")} value={request.house_number} />
                <InfoRow label={t("nearest_landmark")} value={request.nearest_landmark} />
              </div>
            </div>
          )}

          {/* Footer Note */}
          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-center">
            <p className="text-xs text-gray-600">
              {t("public_view_footer")}
            </p>
          </div>
        </div>
      </ResponsiveFormLayout>
    </>
  );
};

export default PublicRequestViewScreen;
